import {Dialog, Button} from '@primer/react'
import {useState} from 'react'
import type {ModelState} from '../../../types'
import {usePlaygroundState} from '../../../contexts/PlaygroundStateContext'
import {ModelsAvatar} from '../../../components/ModelsAvatar'
import {testIdProps} from '@github-ui/test-id-props'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {clsx} from 'clsx'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {supportsTokenCounting} from '../../../utils/model-capability'

export function PlaygroundTokenUsageWidget({modelState}: {modelState: ModelState}) {
  const {lastMessageInputTokens, totalInputTokens, lastMessageOutputTokens, totalOutputTokens} = modelState.tokenUsage
  const usageStats = modelState.usageStats
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const [isOpen, setIsOpen] = useState(false)
  const {models: modelStates} = usePlaygroundState()

  const shouldRenderTokenUsage = useFeatureFlag('github_models_playground_token_usage')
  const renderBorders = modelStates.length > 1

  const handleOpen = () => {
    setIsOpen(!isOpen)
    sendClickAnalyticsEvent({
      category: 'github_models_playground',
      action: 'click_to_open_usage_info',
    })
  }

  if (!shouldRenderTokenUsage) {
    return null
  }

  if (!supportsTokenCounting(modelState.catalogData)) {
    return null
  }

  return (
    <>
      <div className="d-flex flex-items-center" {...testIdProps('playground-token-usage')}>
        <Button variant="invisible" size="small" aria-label="Show usage info" onClick={() => handleOpen()}>
          <span className="fgColor-muted text-small">
            Input: {lastMessageInputTokens}/{totalInputTokens} • Output: {lastMessageOutputTokens}/{totalOutputTokens} •{' '}
            {usageStats.lastMessageLatency}ms
          </span>
        </Button>
      </div>
      {isOpen && (
        <Dialog title="Model usage insights" role="dialog" onClose={() => setIsOpen(false)}>
          <div className="d-flex gap-3">
            {modelStates.map((model, index) => (
              <PlaygroundTokenUsage key={`pg-usage-${index}`} modelState={model} border={renderBorders} />
            ))}
          </div>
        </Dialog>
      )}
    </>
  )
}

export function PlaygroundTokenUsage({
  modelState: {catalogData: model, tokenUsage: usage, usageStats: stats},
  border,
}: {
  modelState: ModelState
  border: boolean
}) {
  return (
    <div className={clsx('d-flex', 'flex-1', 'p-3', border && 'border rounded-2')}>
      <div className="d-flex flex-column gap-3">
        <div className="d-flex gap-2">
          <ModelsAvatar model={model} />
          <span className="text-bold">{model.name}</span>
        </div>
        <div {...testIdProps('playground-usage-input-tokens')}>
          <div className="text-bold">Input Tokens</div>
          <ul className="ml-3 color-fg-muted">
            <li>Most recent message: {usage.lastMessageInputTokens}</li>
            <li>Total: {usage.totalInputTokens}</li>
          </ul>
        </div>
        <div {...testIdProps('playground-usage-output-tokens')}>
          <div className="text-bold">Output Tokens</div>
          <ul className="ml-3 color-fg-muted">
            <li>Most recent message: {usage.lastMessageOutputTokens}</li>
            <li>Total: {usage.totalOutputTokens}</li>
          </ul>
        </div>
        <div {...testIdProps('playground-usage-stats')}>
          <div className="text-bold">Latency</div>
          <ul className="ml-3 color-fg-muted">
            <li>Most recent message: {stats.lastMessageLatency} ms</li>
            <li>Total: {stats.totalLatency} ms</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

try{ PlaygroundTokenUsageWidget.displayName ||= 'PlaygroundTokenUsageWidget' } catch {}
try{ PlaygroundTokenUsage.displayName ||= 'PlaygroundTokenUsage' } catch {}